import { useCallback, useEffect, useState } from "react";
import { Client, Conversation, Message } from "@twilio/conversations";
import { useAuth } from "../context/AuthContext";
import { makeApiRequest, showApiErrorMessages } from "../helpers";
import { ApiResponse } from "../api/apiResponse";
import {
  addConversation,
  getConversationClientToken,
  getConversations,
} from "../api/endpoints/chat/conversations";
import ConversationModel from "../api/models/ConversationModel";

export function useTwilioChat() {
  const [twilioClient, setTwilioClient] = useState<Client>();
  const [conversationsList, setConversationsList] = useState<
    ConversationModel[]
  >([]);
  const { currentUser }: any = useAuth();
  const [unreadMessagesCounterInitial, setUnreadMessagesCounterInitial] =
    useState<number>(0);
  const [messagesList, setMessagesList] = useState<Message[]>([]);

  useEffect(() => {
    getToken();
  }, []);

  const getToken = useCallback(() => {
    makeApiRequest(
      () =>
        getConversationClientToken({
          ActorType: currentUser?.object_type,
          ActorId: currentUser?.id,
        }),
      (response: ApiResponse) => {
        const { token } = response?.data;
        fetchClient(token);
      }
    );
  }, [currentUser?.id]);

  const getUnreadMessagesCount = async (
    sid: string,
    clientProvider?: Client
  ) => {
    if (!!clientProvider && !!sid) {
      const conversation: Conversation =
        await clientProvider.getConversationBySid(sid.trim());
      return conversation.getUnreadMessagesCount();
      //The following has to be further tested
      // const participants = await conversation.getParticipants();
      // const totalMessagesCount = await conversation.getMessagesCount();
      // const lastMessagesIndex =
      //   totalMessagesCount <= 0 ? 0 : totalMessagesCount - 1;
      // let indexOfLastUnRead = 0;

      // if (participants?.[0]?.identity === currentUser?.twilioIdentity) {
      //   indexOfLastUnRead = participants?.[0]?.lastReadMessageIndex ?? 0;
      // } else {
      //   indexOfLastUnRead = participants?.[1]?.lastReadMessageIndex ?? 0;
      // }
      // return lastMessagesIndex - (indexOfLastUnRead ?? 0);
    }
  };

  const getConversationsData = (client: Client) => {
    return makeApiRequest(
      () =>
        getConversations({
          ActorType: currentUser?.object_type,
          ActorId: currentUser?.id,
        }),
      (response: ApiResponse) => {
        let data = ConversationModel.collectionFromApiResponse(response.data);
        handleConversationsList(data ?? [], client);
      }
    );
  };

  const handleConversationsList = async (
    data: ConversationModel[],
    client: Client
  ) => {
    let totalUnreadMessagesCount: number = 0;
    const conversations = await Promise.all(
      data.map(async (conversation: ConversationModel) => {
        let count = await getUnreadMessagesCount(conversation.twilioId, client);
        totalUnreadMessagesCount += count ?? 0;
        conversation.incrementUnreadCount(count ?? 0);
        return conversation;
      })
    );
    setUnreadMessagesCounterInitial(totalUnreadMessagesCount);
    setConversationsList(conversations);
  };

  const addConversationParticipant = (
    participantId: number | string,
    participantType: "student" | "candidate" | "liaison" | "tutor",
    updateConversationList?: (id: string) => void
  ) => {
    makeApiRequest(
      () =>
        addConversation(
          {
            ActorType: currentUser?.object_type,
            ActorId: currentUser?.id,
          },
          {
            participant_id: participantId,
            participant_type: participantType,
          }
        ),
      (response: ApiResponse) => {
        if (response?.isSuccess) {
          const conversation: ConversationModel =
            ConversationModel.fromApiResponse(response.data);
          if (!!updateConversationList) {
            updateConversationList(conversation.twilioId);
          } else {
            window.location.reload();
          }
        }

        if (!response?.isSuccess) {
          showApiErrorMessages(response!);
        }
      }
    );
  };

  const fetchClient = useCallback(
    async (token: string | null) => {
      if (!token) return;

      const client = await new Client(token);

      client.on("initialized", () => {
        // Use the client.
        setTwilioClient(client);
        getConversationsData(client);
      });
      // To catch client initialization errors, subscribe to the `'initFailed'` event.
      client.on("initFailed", ({ error }) => {
        // Handle the error.
      });
    },
    [currentUser]
  );

  const sendMessages: any = async (
    sid: string,
    message: string | File,
    file?: File,
    type?: string
  ) => {
    if (!!twilioClient && type !== "file") {
      await twilioClient
        .getConversationBySid(sid.trim())
        .then(async (conversation) => {
          /**
           *set author_email attribute to known who send this message with email not uuid generated by twillio
           *
           */
          await conversation.sendMessage(message as string, {
            author_email: `${
              currentUser?.email ?? currentUser?.account?.email
            }`,
          });
        });
    } else if (!!twilioClient && !!file) {
      await twilioClient
        .getConversationBySid(sid.trim())
        .then(async (conversation) => {
          const messageBuilder = conversation.prepareMessage();
          const formData = new FormData();

          formData.set(file?.name, file, file?.name);
          messageBuilder.addMedia(formData);
          messageBuilder.setAttributes({
            author_email: `${
              currentUser?.email ?? currentUser?.account?.email
            }`,
            type: type ?? "",
          });
          messageBuilder.build().send();
        });
    }
    setConversationMessagesToReadStatus(sid);
  };

  const getMessages: any = async (sid: string) => {
    if (!!twilioClient) {
      return await twilioClient
        .getConversationBySid(sid.trim())
        .then(async (conversation) => {
          const { items } = await conversation.getMessages(1000);
          setMessagesList(items);
          return items;
        });
    }
  };

  const getActiveConversation = async (sid: string) => {
    if (!!twilioClient) {
      return await twilioClient.getConversationBySid(sid.trim());
    }
  };

  const setConversationMessagesToReadStatus = async (sid: string) => {
    if (!!twilioClient) {
      await twilioClient
        .getConversationBySid(sid.trim())
        .then(async (conversation) => {
          await conversation.setAllMessagesRead();
        });
    }
  };

  const scrollChatCardIntoView = (twilioId: string) => {
    const chatCard = document.getElementById(twilioId);
    if (!!chatCard) {
      chatCard.scrollIntoView({
        behavior: "auto",
        block: "center",
        inline: "center",
      });
    }
  };

  return {
    twilioClient,
    sendMessages,
    getMessages,
    messagesList,
    conversationsList,
    setConversationMessagesToReadStatus,
    getActiveConversation,
    addConversationParticipant,
    getUnreadMessagesCount,
    unreadMessagesCounterInitial,
    getConversationsData,
    scrollChatCardIntoView,
  };
}
